import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import ROUTER from "constants/router";
import { notification } from "reducer/home/action";

// ⚙️ Config Microsoft OAuth2
const msConfig = {
  clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
  tenantId: process.env.REACT_APP_AZURE_TENANT_ID,
  redirectUri: window.location.origin + "/login",
  scope: "openid profile email offline_access",
};

// 🔹 Hàm tạo random string và PKCE
const generateRandomString = (length) => {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const cryptoObj = window.crypto || window.msCrypto;
  const randomValues = new Uint8Array(length);
  cryptoObj.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }
  return result;
};

const sha256 = async (plain) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

// 🟦 Microsoft Icon
const MicrosoftIcon = ({ size = 24, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill={color} />
    <rect x="11" y="1" width="9" height="9" fill={color} />
    <rect x="1" y="11" width="9" height="9" fill={color} />
    <rect x="11" y="11" width="9" height="9" fill={color} />
  </svg>
);

// 🔹 Tạo URL đăng nhập Microsoft
const createAuthUrl = async (forceSelect = false) => {
  const verifier = generateRandomString(64);
  const challenge = await sha256(verifier);
  // Lưu vào localStorage thay vì sessionStorage để tránh mất khi redirect
  localStorage.setItem("pkce_verifier", verifier);

  const params = new URLSearchParams({
    client_id: msConfig.clientId,
    response_type: "code",
    redirect_uri: msConfig.redirectUri,
    response_mode: "query",
    scope: msConfig.scope,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  // Force select account để đảm bảo phải chọn tài khoản mới
  if (forceSelect) {
    params.append("prompt", "select_account");
  }

  return `https://login.microsoftonline.com/${msConfig.tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
};

// 🔹 Đổi authorization_code lấy token
const fetchAccessToken = async (code) => {
  const verifier = localStorage.getItem("pkce_verifier");

  if (!verifier) {
    throw new Error("PKCE verifier not found");
  }

  const params = new URLSearchParams({
    client_id: msConfig.clientId,
    scope: msConfig.scope,
    code,
    redirect_uri: msConfig.redirectUri,
    grant_type: "authorization_code",
    code_verifier: verifier,
  });

  const res = await fetch(`https://login.microsoftonline.com/${msConfig.tenantId}/oauth2/v2.0/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params,
  });

  const data = await res.json();

  if (!res.ok) {
    console.error("Token exchange error:", data);
    throw new Error(data.error_description || data.error || "Failed to exchange token");
  }

  // Xóa verifier sau khi sử dụng xong
  localStorage.removeItem("pkce_verifier");

  return data;
};

function decodeJWT(token) {
  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Token không hợp lệ");

  const header = JSON.parse(atob(parts[0].replace(/-/g, "+").replace(/_/g, "/")));
  const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));

  return { header, payload };
}

// 🔹 Hàm lưu sid vào localStorage
const saveSidToLocalStorage = (idToken) => {
  try {
    const { payload } = decodeJWT(idToken);
    if (payload.sid) {
      localStorage.setItem("ms_sid", payload.sid);
    }
  } catch (error) {
    console.error("❌ Lỗi khi lưu sid:", error);
  }
};

// 🔹 Hàm xóa thông tin tài khoản Microsoft đang đăng nhập
export const clearMicrosoftSession = () => {
  // Xóa tài khoản Microsoft hiện tại từ localStorage
  localStorage.removeItem("ms_account");

  // Xóa PKCE verifier từ localStorage (liên quan đến session hiện tại)
  localStorage.removeItem("pkce_verifier");

  // Xóa sid
  localStorage.removeItem("ms_sid");
};

// 🔹 Hàm logout chính thức từ Microsoft (xóa session trên toàn hệ thống Microsoft)
export const logoutFromMicrosoft = async (accessToken) => {
  // Lấy id_token từ ms_account trước khi xóa
  const accountStr = localStorage.getItem("ms_account");
  let idToken = null;

  if (accountStr) {
    try {
      const account = JSON.parse(accountStr);
      idToken = account.username; // username chứa id_token
    } catch (e) {
      console.error("Failed to parse ms_account:", e);
    }
  }

  // Bước 1: Gọi Microsoft Graph API để revoke tất cả sessions và refresh tokens
  if (accessToken) {
    try {
      const graphResponse = await fetch("https://graph.microsoft.com/v1.0/me/revokeSignInSessions", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (graphResponse.ok) {
        const result = await graphResponse.json();
      } else {
        const error = await graphResponse.json();
        console.error("❌ Failed to revoke sessions:", error);
      }
    } catch (error) {
      console.error("❌ Error calling Graph API:", error);
    }
  }

  // Tạm dừng ở đây 1 phút để check
  // code tạm dừng 1 phút
  // await new Promise((resolve) => setTimeout(resolve, 60000));

  // Bước 2: Xóa session local
  clearMicrosoftSession();

  // Bước 3: Đánh dấu đang trong quá trình logout
  sessionStorage.setItem("ms_post_logout", "true");

  // Bước 4: Redirect đến Microsoft logout endpoint
  const logoutEndpoint = `https://login.microsoftonline.com/common/oauth2/v2.0/logout`;
  const params = new URLSearchParams({
    post_logout_redirect_uri: msConfig.redirectUri,
  });

  if (idToken) {
    params.append("id_token_hint", idToken);
  }

  const logoutUrl = `${logoutEndpoint}?${params.toString()}`;

  // Redirect đến endpoint logout của Microsoft
  window.location.href = logoutUrl;
};

const MicrosoftLogin = ({ loginLDAP, history, devMode = false }) => {
  const [account, setAccount] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const dispatch = useDispatch();
  const hasInitialized = useRef(false); // Flag để tránh chạy lại

  // 🔹 Redirect sang màn hình đăng nhập Microsoft
  const redirectToMicrosoftLogin = async () => {
    // Nếu devMode thì không redirect, chỉ dừng loading
    if (devMode) {
      setIsChecking(false);
      return;
    }
    // Xóa hoàn toàn session Microsoft trước khi redirect
    clearMicrosoftSession();
    // Xóa flag post-logout nếu có
    sessionStorage.removeItem("ms_post_logout");
    // Force select account để đảm bảo phải đăng nhập lại từ đầu
    const authUrl = await createAuthUrl(true);
    window.location.href = authUrl;
  };

  // 🟩 Khi vào trang, xử lý login flow
  useEffect(() => {
    // Nếu đã chạy rồi thì không chạy lại
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const init = async () => {
      // Kiểm tra flag post-logout từ sessionStorage
      const isPostLogout = sessionStorage.getItem("ms_post_logout") === "true";

      // Kiểm tra xem có code từ Microsoft redirect về không
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");

      // Nếu vừa logout xong (dựa vào sessionStorage flag)
      if (isPostLogout) {
        sessionStorage.removeItem("ms_post_logout");
        setIsChecking(false);
        // Redirect sang Microsoft login với prompt select_account
        setTimeout(() => {
          redirectToMicrosoftLogin();
        }, 500);
        return;
      }

      if (code) {
        // Có code -> xử lý callback từ Microsoft OAuth
        setIsChecking(true);
        try {
          const tokenData = await fetchAccessToken(code);
          const user = { username: tokenData.id_token, token: tokenData.access_token };
          localStorage.setItem("ms_account", JSON.stringify(user));

          // Lưu sid vào localStorage
          saveSidToLocalStorage(tokenData.id_token);

          setAccount(user);

          // Xóa code khỏi URL
          window.history.replaceState({}, document.title, window.location.pathname);

          // Gọi API loginLDAP
          const res = await dispatch(loginLDAP({ code: tokenData.access_token }));
          // console.log("Login LDAP result 1:", res);
          if (res) {
            notification({ type: "success", message: "Đăng nhập thành công!" });
            // Sử dụng window.location.href thay vì history.push để force reload
            setTimeout(() => {
              window.location.href = ROUTER.HOME;
            }, 100);
            setIsChecking(false);
          } else {
            notification({ type: "error", message: "Đăng nhập không thành công!" });
            localStorage.removeItem("ms_account");
            setAccount(null);
            setTimeout(() => {
              redirectToMicrosoftLogin();
            }, 1000);
          }
        } catch (error) {
          setIsChecking(false);
          console.error("OAuth callback error:", error);
          notification({ type: "error", message: error.message || "Đăng nhập thất bại!" });
          setTimeout(() => {
            redirectToMicrosoftLogin();
          }, 1000);
        }
        return; // Dừng lại, không check tài khoản cũ
      }

      // Không có code -> check xem có tài khoản Microsoft đã lưu không
      const acc = localStorage.getItem("ms_account");
      if (acc) {
        // Có tài khoản Microsoft active -> tự động đăng nhập
        const user = JSON.parse(acc);
        setAccount(user);
        setIsChecking(true);

        try {
          const res = await dispatch(loginLDAP({ code: user.token }));
          // console.log("Login LDAP result 2:", res);
          if (res) {
            notification({ type: "success", message: "Đăng nhập thành công!" });
            setTimeout(() => {
              window.location.href = ROUTER.HOME;
            }, 100);
            setIsChecking(false);
          } else {
            // Token hết hạn hoặc không hợp lệ -> xóa và redirect
            localStorage.removeItem("ms_account");
            setAccount(null);
            redirectToMicrosoftLogin();
          }
        } catch (error) {
          setIsChecking(false);
          localStorage.removeItem("ms_account");
          setAccount(null);
          redirectToMicrosoftLogin();
        }
      } else {
        // Không có tài khoản Microsoft active -> redirect sang màn hình đăng nhập Microsoft
        setIsChecking(false);
        redirectToMicrosoftLogin();
      }
    };

    init();
  }, []); // Chỉ chạy 1 lần khi mount

  const { header, payload } = account ? decodeJWT(account?.username) : { header: null, payload: null };

  // Hiển thị loading khi đang kiểm tra/đăng nhập tự động
  if (isChecking) {
    return (
      <div
        style={{
          padding: "24px",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          maxWidth: "400px",
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .spinner {
              animation: spin 1s linear infinite;
            }
          `}
        </style>
        <div style={{ marginBottom: "16px" }} className="spinner">
          <MicrosoftIcon size={48} color="#0078d4" />
        </div>
        <h3
          style={{
            color: "#2c3e50",
            marginBottom: "8px",
            fontSize: "18px",
            fontWeight: "600",
          }}
        >
          Đang đăng nhập...
        </h3>
        <p
          style={{
            color: "#7f8c8d",
            fontSize: "14px",
            margin: "0",
          }}
        >
          Vui lòng chờ trong giây lát
        </p>
      </div>
    );
  }

  return null; // Component này sẽ redirect ngay, không cần render gì thêm
};

export default MicrosoftLogin;

